import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useGetImportsQuery } from '../../../../api/schedulingApi';
import type { ImportStatus } from '../../../../types/scheduling';

interface ImportHistoryProps {
  scheduleId: string;
  studioId: string;
}

const statusColor = (
  status: ImportStatus
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'committed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'undone') return 'default';
  return 'warning';
};

/** Read-only list of past imports for the schedule. */
const ImportHistory: React.FC<ImportHistoryProps> = ({
  scheduleId,
  studioId,
}) => {
  const { data: imports } = useGetImportsQuery(
    { scheduleId, studioId },
    { skip: !scheduleId || !studioId }
  );

  if (!imports || imports.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Import history
      </Typography>
      <Stack spacing={1}>
        {imports.map(row => (
          <Stack
            key={row.id}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ flexWrap: 'wrap' }}
          >
            <Chip
              size="small"
              label={row.status}
              color={statusColor(row.status)}
              variant="outlined"
            />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 120 }}>
              {row.file_name || row.source_type}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(row.created_at).toLocaleDateString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default ImportHistory;
